import * as React from 'react'
import cn from 'classnames'
import { CaretDown } from '@phosphor-icons/react'
import { CheckinType, Recurrence } from './types'
import { recurrenceLabel } from './utils'
import {
  fieldLabelClass,
  baseInputClass,
  inputClass,
  FieldError,
  TIME_OPTIONS,
  TIMEZONE_OPTIONS,
  NAME_MAX_LENGTH,
} from './form-helpers'

export const RECURRENCE_OPTIONS: Recurrence[] = [
  'one_time',
  'monthly',
  'quarterly',
  'bi_annually',
  'annually',
]

export const CHECKIN_TYPE_OPTIONS: Array<{
  value: CheckinType
  title: string
  description: string
}> = [
  {
    value: 'standard',
    title: 'Standard Check-in',
    description: 'Check-in on skills against the position',
  },
  {
    value: 'performance',
    title: 'Performance Review',
    description:
      'Check-in on skills and other competencies. This includes the custom questions made by your organisation',
  },
]

type StepOneProps = {
  name: string
  onNameChange: (value: string) => void
  nameError: string | null
  startDate: string
  onStartDateChange: (value: string) => void
  startDateError: string | null
  endDate: string
  onEndDateChange: (value: string) => void
  endDateError: string | null
  startTime: string
  onStartTimeChange: (value: string) => void
  startTimeError: string | null
  timezone: string
  onTimezoneChange: (value: string) => void
  recurrence: Recurrence
  onRecurrenceChange: (value: Recurrence) => void
  recStartDate: string
  onRecStartDateChange: (value: string) => void
  recStartDateError: string | null
  recEndDate: string
  onRecEndDateChange: (value: string) => void
  recEndDateError: string | null
  recStartTime: string
  onRecStartTimeChange: (value: string) => void
  recStartTimeError: string | null
  recTimezone: string
  onRecTimezoneChange: (value: string) => void
  checkinType: CheckinType
  onCheckinTypeChange: (value: CheckinType) => void
}

// Step 1: cycle name, schedule, recurrence, and check-in type. Split out of
// NewCycleModal (which otherwise owns every step's JSX inline) purely to keep
// that component's render tree — and cognitive complexity — down to a single
// step at a time, matching how steps 2-4 are already split into their own
// components.
export const StepOne = ({
  name,
  onNameChange,
  nameError,
  startDate,
  onStartDateChange,
  startDateError,
  endDate,
  onEndDateChange,
  endDateError,
  startTime,
  onStartTimeChange,
  startTimeError,
  timezone,
  onTimezoneChange,
  recurrence,
  onRecurrenceChange,
  recStartDate,
  onRecStartDateChange,
  recStartDateError,
  recEndDate,
  onRecEndDateChange,
  recEndDateError,
  recStartTime,
  onRecStartTimeChange,
  recStartTimeError,
  recTimezone,
  onRecTimezoneChange,
  checkinType,
  onCheckinTypeChange,
}: StepOneProps) => (
  <>
    <div className="mb-6">
      <label htmlFor="cycle-name" className={fieldLabelClass}>
        Cycle name
      </label>
      <input
        id="cycle-name"
        type="text"
        value={name}
        maxLength={NAME_MAX_LENGTH}
        onChange={(event) => onNameChange(event.target.value)}
        placeholder="e.g. Q2 Position Check-in"
        className={inputClass(Boolean(nameError))}
      />
      <FieldError message={nameError} />
    </div>

    <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div>
        <label htmlFor="start-date" className={fieldLabelClass}>
          Start Date
        </label>
        <input
          id="start-date"
          type="date"
          value={startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
          className={inputClass(Boolean(startDateError))}
        />
        <FieldError message={startDateError} />
      </div>

      <div>
        <label htmlFor="end-date" className={fieldLabelClass}>
          End Date
        </label>
        <input
          id="end-date"
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(event) => onEndDateChange(event.target.value)}
          className={inputClass(Boolean(endDateError))}
        />
        <FieldError message={endDateError} />
      </div>
    </div>

    <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div>
        <label htmlFor="start-time" className={fieldLabelClass}>
          Start Time
        </label>
        <div className="relative">
          <select
            id="start-time"
            value={startTime}
            onChange={(event) => onStartTimeChange(event.target.value)}
            className={cn(
              inputClass(Boolean(startTimeError)),
              'appearance-none pr-11',
            )}
          >
            {TIME_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <CaretDown
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
            size={18}
          />
        </div>
        <FieldError message={startTimeError} />
      </div>

      <div>
        <label htmlFor="timezone" className={fieldLabelClass}>
          Time Zone
        </label>
        <div className="relative">
          <select
            id="timezone"
            value={timezone}
            onChange={(event) => onTimezoneChange(event.target.value)}
            className={cn(
              baseInputClass,
              'appearance-none pr-11 focus:ring-[#2E9E5D]',
            )}
          >
            {TIMEZONE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <CaretDown
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
            size={18}
          />
        </div>
      </div>
    </div>

    <div className="mb-6">
      <p className={cn(fieldLabelClass, 'mb-2')}>Recurrence</p>
      <div className="flex flex-wrap gap-3">
        {RECURRENCE_OPTIONS.map((option) => {
          const isActive = recurrence === option

          return (
            <button
              key={option}
              type="button"
              onClick={() => onRecurrenceChange(option)}
              aria-pressed={isActive}
              className={cn(
                'rounded-full border border-solid px-3 py-2 font-talent text-[16px] font-normal leading-[20px] transition-colors',
                isActive
                  ? '!border-[#2E9E5D] bg-[#2E9E5D] text-white'
                  : '!border-[#D7DBE8] bg-white text-[#1D264A] hover:!border-[#2E9E5D]',
              )}
            >
              {recurrenceLabel(option)}
            </button>
          )
        })}
      </div>
    </div>

    {recurrence !== 'one_time' && (
      <>
        <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="rec-start-date" className={fieldLabelClass}>
              Recurrence Start Date
            </label>
            <input
              id="rec-start-date"
              type="date"
              value={recStartDate}
              min={startDate || undefined}
              max={endDate || undefined}
              onChange={(event) => onRecStartDateChange(event.target.value)}
              className={inputClass(Boolean(recStartDateError))}
            />
            <FieldError message={recStartDateError} />
          </div>

          <div>
            <label htmlFor="rec-end-date" className={fieldLabelClass}>
              Recurrence End Date
            </label>
            <input
              id="rec-end-date"
              type="date"
              value={recEndDate}
              min={recStartDate || startDate || undefined}
              max={endDate || undefined}
              onChange={(event) => onRecEndDateChange(event.target.value)}
              className={inputClass(Boolean(recEndDateError))}
            />
            <FieldError message={recEndDateError} />
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="rec-start-time" className={fieldLabelClass}>
              Recurrence Start Time
            </label>
            <div className="relative">
              <select
                id="rec-start-time"
                value={recStartTime}
                onChange={(event) => onRecStartTimeChange(event.target.value)}
                className={cn(
                  inputClass(Boolean(recStartTimeError)),
                  'appearance-none pr-11',
                )}
              >
                {TIME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <CaretDown
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
                size={18}
              />
            </div>
            <FieldError message={recStartTimeError} />
          </div>

          <div>
            <label htmlFor="rec-timezone" className={fieldLabelClass}>
              Recurrence Time Zone
            </label>
            <div className="relative">
              <select
                id="rec-timezone"
                value={recTimezone}
                onChange={(event) => onRecTimezoneChange(event.target.value)}
                className={cn(
                  baseInputClass,
                  'appearance-none pr-11 focus:ring-[#2E9E5D]',
                )}
              >
                {TIMEZONE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <CaretDown
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#5E6580]"
                size={18}
              />
            </div>
          </div>
        </div>
      </>
    )}

    <div className="mb-2">
      <p className={fieldLabelClass}>Check-in Type</p>
      <div className="flex flex-col gap-3">
        {CHECKIN_TYPE_OPTIONS.map((option) => {
          const isActive = checkinType === option.value

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onCheckinTypeChange(option.value)}
              aria-pressed={isActive}
              className={cn(
                'flex items-start gap-3 rounded-lg border border-solid px-5 py-4 text-left transition-colors',
                // Bootstrap's legacy `.border` utility (still imported in
                // main.scss) sets `border: ... !important`, which beats
                // Tailwind's non-important border-color utility outright —
                // the `!` modifier marks these important too so they win.
                isActive
                  ? '!border-[#2E9E5D]'
                  : '!border-[#E5E7EB] hover:!border-[#D7DBE8]',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                  isActive
                    ? 'bg-[#2E9E5D]'
                    : 'border-2 border-solid border-[#C7CCDD]',
                )}
              >
                {isActive && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
              <span>
                <span className="block font-talent text-[16px] font-medium leading-[20px] text-[#1D264A]">
                  {option.title}
                </span>
                <span className="mt-0.5 block font-talent text-[14px] font-normal leading-[16px] tracking-[0.3px] text-[#7B8197]">
                  {option.description}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  </>
)
